import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import config from "../../config";

const KMLDataTable = () => {
    const [fullData, setFullData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [batchSize, setBatchSize] = useState(10);
    const [batchNumber, setBatchNumber] = useState(1);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [failedBatches, setFailedBatches] = useState([]);
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState("");
    const [datasetOptions] = useState(["Division.json","Ranges.json", "Rounds.json", "Beats.json","Comps.json"]); 
    const [divisionOptions] = useState(["Buffer", "Central Chanda", "Bramhpuri", "TATR(CORE)","Chandrapur"]);
    const [selectedDataset2, setSelectedDataset2] = useState("");

    const fetchData = async (fileName) => {
        try {
            const response = await fetch(`${fileName}`);
            if (!response.ok) throw new Error(`Failed to load ${fileName}`);
            const jsonData = await response.json();

            const cleanData = jsonData.filter(item =>
                !Object.values(item).some(value => value === undefined || value === null || value === "Unknown")
            );

            setFullData(cleanData);
            setFilteredData(cleanData);  // initially show full set
            setBatchNumber(1);

            if (cleanData.length > 0) {
                const dynamicColumns = Object.keys(cleanData[0])
                    .filter((key) => key !== "coordinates")
                    .map((key) => ({
                        name: key.replace(/_/g, " ").toUpperCase(),
                        selector: (row) => row[key],
                        sortable: true,
                        wrap: true,
                    }));
                setColumns(dynamicColumns);
            }
        } catch (error) {
            console.error(`Error loading ${fileName}:`, error);
            alert(`Failed to load ${fileName}`);
            setFullData([]);
            setFilteredData([]);
            setColumns([]);
        }
    };

    useEffect(() => {
        if (selectedDataset) fetchData(selectedDataset);
    }, [selectedDataset]);

    useEffect(() => {
        if (!selectedDataset2) {
            setFilteredData(fullData);
        } else {
            const divisionFiltered = fullData.filter(item => item.divisionName === selectedDataset2);
            setFilteredData(divisionFiltered);
        }
    }, [selectedDataset2, fullData]);

    const uploadBatches = async (retryBatches = []) => {
        setIsUploading(true);
        setFailedBatches([]);
        setUploadProgress(0);

        const totalBatches = Math.ceil(filteredData.length / batchSize);
        const batchIndices = retryBatches.length > 0 ? retryBatches : Array.from({ length: totalBatches }, (_, i) => i);

        for (const batchIndex of batchIndices) {
            const start = batchIndex * batchSize;
            const batch = filteredData.slice(start, start + batchSize);

            if (batch.length === 0) continue;

            const filteredBatch = batch.map(item =>
                Object.fromEntries(Object.entries(item).filter(([_, value]) =>
                    value !== undefined && value !== null && value !== "Unknown"))
            );

            try {
                const response = await fetch(`${config.API_URL}/common`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("tokens")
                            ? JSON.parse(localStorage.getItem("tokens") ?? "{}")?.AuthenticationResult?.AccessToken ?? ""
                            : "",
                    },
                    body: JSON.stringify({
                        act: "common",
                        subact: "uploadInBatch",
                        info: {
                            tableName: `Controlroom-${selectedDataset.replace(".json", "")}-Master-dev`,
                            data: filteredBatch,
                        },
                    }),
                });

                if (!response.ok) {
                    console.error(`Batch ${batchIndex + 1} failed.`);
                    setFailedBatches((prev) => [...prev, batchIndex]);
                }

                setUploadProgress(((batchIndex + 1) / totalBatches) * 100);
            } catch (error) {
                console.error(`Error uploading batch ${batchIndex + 1}:`, error);
                setFailedBatches((prev) => [...prev, batchIndex]);
            }
        }

        setIsUploading(false);
        alert("Upload completed. Check for failed batches.");
    };

    const uploadSpecificBatch = async () => {
        const start = (batchNumber - 1) * batchSize;
        const batch = filteredData.slice(start, start + batchSize);

        if (batch.length === 0) {
            alert("Invalid batch number.");
            return;
        }

        setIsUploading(true);
        try {
            const response = await fetch(`${config.API_URL}/common`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("tokens")
                        ? JSON.parse(localStorage.getItem("tokens") ?? "{}")?.AuthenticationResult?.AccessToken ?? ""
                        : "",
                },
                body: JSON.stringify({
                    act: "common",
                    subact: "uploadInBatch",
                    info: {
                        tableName: `Controlroom-${selectedDataset.replace(".json", "")}-Master-dev`,
                        data: batch,
                    },
                }),
            });

            if (!response.ok) {
                console.error(`Batch ${batchNumber} failed.`);
                setFailedBatches((prev) => [...prev, batchNumber - 1]);
                alert(`Batch ${batchNumber} upload failed.`);
            } else {
                alert(`Batch ${batchNumber} uploaded successfully.`);
            }
        } catch (error) {
            console.error(`Error uploading batch ${batchNumber}:`, error);
        }

        setIsUploading(false);
    };

    const retryFailedBatches = () => {
        if (selectedBatches.length === 0) {
            alert("Please select at least one failed batch to retry.");
            return;
        }

        uploadBatches(selectedBatches);
        setSelectedBatches([]);
    };

    const toggleBatchSelection = (batchIndex) => {
        setSelectedBatches((prev) =>
            prev.includes(batchIndex) ? prev.filter((i) => i !== batchIndex) : [...prev, batchIndex]
        );
    };

    const customStyles = {
        headCells: {
            style: {
                fontSize: "12px",
                padding: "4px 6px",
                backgroundColor: "#f1f1f1",
                position: "sticky",
                top: 0,
                zIndex: 2,
            },
        },
        cells: {
            style: {
                fontSize: "11px",
                padding: "4px 8px",
            },
        },
        rows: {
            style: {
                minHeight: "28px",
            },
        },
        pagination: {
            style: {
                fontSize: "12px",
                padding: "4px 8px",
            },
        },
    };

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs title="Control Room" breadcrumbItem="Upload Json" />
                <div style={{ padding: "8px" }}>
                    <div style={{ marginBottom: "10px" }}>
                        <label>Select Dataset: </label>
                        <select value={selectedDataset} onChange={(e) => setSelectedDataset(e.target.value)}>
                            <option value="">Select Dataset</option>
                            {datasetOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>

                        <label style={{ marginLeft: "10px" }}>Select Division: </label>
                        <select value={selectedDataset2} onChange={(e) => setSelectedDataset2(e.target.value)}>
                            <option value="">All Divisions</option>
                            {divisionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>

                        <label style={{ marginLeft: "10px" }}>Batch Size: </label>
                        <select value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value))}>
                            {[5, 10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                        </select>

                        <label style={{ marginLeft: "10px" }}>Batch #: </label>
                        <input
                            type="number"
                            value={batchNumber}
                            min={1}
                            max={Math.ceil(filteredData.length / batchSize)}
                            onChange={(e) => setBatchNumber(parseInt(e.target.value))}
                            style={{ width: 60 }}
                        />

                        <button onClick={uploadSpecificBatch} disabled={!selectedDataset || isUploading}>Upload Batch</button>
                        <button onClick={() => uploadBatches()} disabled={!selectedDataset || isUploading}>Upload All</button>
                    </div>

                    {isUploading && <p>Uploading... {Math.round(uploadProgress)}%</p>}

                    {failedBatches.length > 0 && (
                        <>
                            <button onClick={retryFailedBatches} disabled={selectedBatches.length === 0 || isUploading}>
                                Retry Selected
                            </button>
                            <div>
                                <h4>Failed Batches:</h4>
                                {failedBatches.map((index) => (
                                    <label key={index}>
                                        <input
                                            type="checkbox"
                                            checked={selectedBatches.includes(index)}
                                            onChange={() => toggleBatchSelection(index)}
                                        />
                                        Batch {index + 1}
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {filteredData.length > 0 ? (
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            pagination
                            customStyles={customStyles}
                            highlightOnHover
                            striped
                            dense
                            noHeader
                        />
                    ) : (
                        <p>Select a dataset to display data.</p>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default KMLDataTable;
